'use client'

import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, Card, Loader, Spacer } from '@vinted/web-ui'

import { useSession } from '@marketplace-web/shared/session'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ConditionalWrapper } from '@marketplace-web/shared/ui-helpers'
import { useAbTest } from '@marketplace-web/shared/ab-tests'

import { setBusinessAccountPolicies } from 'data/api/set-business-account-policies'
import ProfileSideNavigation from 'components/ProfileSideNavigation'

import LegalImprint from './LegalImprint'
import PoliciesTextarea from './PoliciesTextarea'
import useFetchPoliciesSettingsData from './useFetchPoliciesSettingsData'

const TRANSLATE_PREFIX = 'policies_settings'

enum Field {
  TermsAndConditions = 'termsAndConditions',
  ReturnPolicy = 'returnPolicy',
  AdditionalInformation = 'additionalInformation',
}

type FormModel = {
  termsAndConditions: string
  returnPolicy: string
  additionalInformation: string
}

const PoliciesSettings = () => {
  const { user, isWebview: isWebView } = useSession()
  const businessAccountId = user?.business_account?.id as number

  const [policies, isLoading] = useFetchPoliciesSettingsData()
  const translate = useTranslate(TRANSLATE_PREFIX)

  const settingsBackgroundUpdateAbTest = useAbTest('settings_background_update_web')
  const isSettingsBackgroundUpdateAbTestEnabled = settingsBackgroundUpdateAbTest?.variant === 'on'

  const formMethods = useForm<FormModel>({
    defaultValues: {
      termsAndConditions: '',
      returnPolicy: '',
      additionalInformation: '',
    },
  })

  const { reset, handleSubmit, formState } = formMethods

  useEffect(() => {
    reset(policies)
  }, [reset, policies])

  if (!businessAccountId) return null

  const onSubmit = async ({
    termsAndConditions,
    returnPolicy,
    additionalInformation,
  }: FormModel) => {
    await setBusinessAccountPolicies({
      businessAccountId,
      termsAndConditions,
      returnPolicy,
      additionalInformation,
    })
  }

  const renderWrapper = (children: JSX.Element) => <Card>{children}</Card>

  if (isLoading) {
    return (
      <div className="u-flexbox u-justify-content-center">
        <Loader testId="policies-loader" />
      </div>
    )
  }

  return (
    <>
      {!isWebView && <ProfileSideNavigation />}
      <div className="body-content__content">
        <Spacer size={Spacer.Size.Large} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ConditionalWrapper
              condition={isSettingsBackgroundUpdateAbTestEnabled}
              wrapper={renderWrapper}
            >
              <LegalImprint />
            </ConditionalWrapper>
            <Spacer size={Spacer.Size.XLarge} />
            <ConditionalWrapper
              condition={isSettingsBackgroundUpdateAbTestEnabled}
              wrapper={renderWrapper}
            >
              <PoliciesTextarea
                name={Field.TermsAndConditions}
                title={translate('terms_and_conditions.title')}
                placeholder={translate('terms_and_conditions.placeholder')}
              />
            </ConditionalWrapper>
            <Spacer size={Spacer.Size.XLarge} />
            <ConditionalWrapper
              condition={isSettingsBackgroundUpdateAbTestEnabled}
              wrapper={renderWrapper}
            >
              <PoliciesTextarea
                name={Field.ReturnPolicy}
                title={translate('return_policy.title')}
                placeholder={translate('return_policy.placeholder')}
              />
            </ConditionalWrapper>
            <Spacer size={Spacer.Size.XLarge} />
            <ConditionalWrapper
              condition={isSettingsBackgroundUpdateAbTestEnabled}
              wrapper={renderWrapper}
            >
              <PoliciesTextarea
                name={Field.AdditionalInformation}
                title={translate('additional_information.title')}
                placeholder={translate('additional_information.placeholder')}
              />
            </ConditionalWrapper>
            <Spacer size={Spacer.Size.Large} />
            <div className="u-float-right">
              <Button
                type={Button.Type.Submit}
                styling={Button.Styling.Filled}
                size={Button.Size.Medium}
                disabled={formState.isSubmitting}
                isLoading={formState.isSubmitting}
                inline
                onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
                  if (formState.isSubmitting) {
                    event.preventDefault()
                  }
                }}
              >
                {translate('actions.submit')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  )
}

export default PoliciesSettings
